import { SidePanel, type SidePanelProps } from "$root/components/common/SidePanelV2";
import { DraggableSettingsPanel } from "$root/ui-lib/DraggableSettingsPanel";
import { Column, Controller, Row } from "@mdotm/shadcn/legacy/components";
import {
	type ImperativeHandleRefProps,
	type SpawnResult,
	adaptAnimatedNodeProvider,
	spawn,
} from "@mdotm/shadcn/legacy/react-extensions";
import { SidePanelListItem } from "$root/functional-areas/workspace/ui/SidePanelItem";
import { Divider } from "$root/components/Divider";

export type ColumnMetadata<T extends string> = {
	id: T;
	checked: boolean;
	label: string;
	disabled?: boolean;
	hidden?: boolean;
};

export type ColumnChooserProps<T extends string> = ImperativeHandleRefProps<{
	getColumns(): ColumnMetadata<T>[];
}> & {
	columns: ColumnMetadata<T>[];
	onChange?(newColum: ColumnMetadata<T>[]): void;
	title?: string;
	description?: string;
};

type SidePanelColumnChooserProps<T extends string> = ColumnChooserProps<T> &
	Pick<
		Extract<
			SidePanelProps,
			{
				mode: "overlay";
			}
		>,
		"open" | "onClose" | "onAnimationStateChange"
	>;

export function SidePanelColumnChooser<T extends string>({
	columns,
	onChange,
	title = "Table Settings",
	description = "Manage columns and table display options",
	...sidePanelProps
}: SidePanelColumnChooserProps<T>): React.JSX.Element {
	return (
		<SidePanel mode="overlay" position="right" {...sidePanelProps}>
			<Controller value={columns}>
				{(_columns) => (
					<DraggableSettingsPanel
						title={title}
						description={description}
						// onBack={sidePanelProps.onClose ? { icon: "Close", action: sidePanelProps.onClose } : undefined}
						items={_columns.value}
						itemKey={(item) => `${item.id}-${item.checked}`}
						onReorder={(newItems) => {
							_columns.onChange(newItems);
							onChange?.(newItems);
						}}
						searchExtract={(item) => item.label}
					>
						{(item, { dragIcon, isDragging }) => {
							if (item.hidden) {
								return null;
							}
							return (
								<Column className="overflow-hidden">
									<Row gap={8} alignItems="center">
										{dragIcon}
										<SidePanelListItem
											title={item.label}
											disableToggle={item.disabled}
											checked={item.checked}
											onToggle={(checked) => {
												const newValue = _columns.value.map((c) => (c.id === item.id ? { ...c, checked } : c));
												_columns.onChange(newValue);
												onChange?.(newValue);
											}}
										/>
									</Row>
									{!isDragging && <Divider className="border-dashed" />}
								</Column>
							);
						}}
					</DraggableSettingsPanel>
				)}
			</Controller>
		</SidePanel>
	);
}

export function spawnColumnChooser<T extends string>(params: ColumnChooserProps<T>): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ onHidden, resolve, show }) => (
			<SidePanelColumnChooser<T>
				{...params}
				open={show}
				onClose={() => resolve()}
				onAnimationStateChange={(s) => s === "exited" && onHidden()}
			/>
		)),
	);
}
